import { compact } from 'lodash';
import {
  type Currency,
  FreightType,
  type VoyageIncome_VoyageCharterFragment,
} from '../../../graphql';
import { type LoadedAmount } from '../loadedAmounts';
import { type Income } from './income';
import { convertToDefaultCurrency } from '../../../state/zustand';
import type { ScenarioOrder } from '../../../lib/planningData';

type ExtraFreight = {
  id: number;
  name: string | null;
  amount: number;
  type: FreightType | null;
  currency: Currency | null;
  description: string | null;
  ignoreInPnl: boolean | null;
};

function getVoyageCharterExtraFreights(
  voyageCharter: VoyageIncome_VoyageCharterFragment,
): ExtraFreight[] {
  return compact(
    voyageCharter.voyageCharterExtraFreightsByVoyageCharterId.nodes,
  ).map(({ id, amount, name, type, description, ignoreInPnl }) => ({
    id,
    amount: Number(amount),
    name: name ?? null,
    type: type ?? null,
    currency: voyageCharter.currency ?? null,
    description: description ?? null,
    ignoreInPnl: ignoreInPnl ?? null,
  }));
}

export function getVoyageCharterExtraFreightIncome(
  voyageCharter: VoyageIncome_VoyageCharterFragment,
  loadedAmounts: LoadedAmount[],
  freightIncomeByOrderId: Map<number, number>,
  orderVcRelsByIncomingVcId: Map<
    number,
    ScenarioOrder['incomingVoyageCharterRelation'][]
  >,
): Income[] {
  const incomes: Income[] = [];

  const orderIds = new Set<number>();
  for (const vcRel of orderVcRelsByIncomingVcId.get(voyageCharter.id) ?? []) {
    if (vcRel) {
      orderIds.add(vcRel.orderId);
    }
  }

  const extraFreights = getVoyageCharterExtraFreights(voyageCharter);

  extraFreights.forEach((extraFreight) => {
    if (extraFreight.ignoreInPnl) {
      return;
    }
    switch (extraFreight.type) {
      case FreightType.LUMPSUM: {
        const convertedAmount = extraFreight.currency
          ? convertToDefaultCurrency(extraFreight.amount, extraFreight.currency)
          : extraFreight.amount;

        incomes.push({
          type: 'EXTRA_FREIGHT',
          name: extraFreight.name ?? `Extra freight ${extraFreight.id}`,
          value: convertedAmount,
          orderId: null,
          voyageCharterId: voyageCharter.id,
          params: [
            {
              name: 'Extra freight type',
              value: 'Lump sum',
            },
            {
              name: 'Amount',
              value: extraFreight.amount,
            },
            {
              name: 'Description',
              value: extraFreight.description,
            },
          ],
        });
        break;
      }
      case FreightType.RATE: {
        const totalMass = loadedAmounts
          .filter((loaded) => orderIds.has(loaded.orderId))
          .reduce((sum, loaded) => sum + loaded.loadedMass, 0);

        const amount = totalMass * extraFreight.amount;

        const convertedAmount = extraFreight.currency
          ? convertToDefaultCurrency(amount, extraFreight.currency)
          : amount;

        incomes.push({
          type: 'EXTRA_FREIGHT',
          name: extraFreight.name ?? `Extra freight ${extraFreight.id}`,
          value: convertedAmount,
          orderId: null,
          voyageCharterId: voyageCharter.id,
          params: [
            {
              name: 'Extra freight type',
              value: 'Rate',
            },
            {
              name: 'Rate',
              value: extraFreight.amount,
            },
            {
              name: 'Total mass (mt)',
              value: totalMass,
            },
            {
              name: 'Description',
              value: extraFreight.description,
            },
          ],
        });
        break;
      }
      case FreightType.PERCENTAGE: {
        const totalFreightIncome = Array.from(orderIds).reduce(
          (total, orderId) => {
            const freightByOrderId = freightIncomeByOrderId.get(orderId);
            if (freightByOrderId) {
              return total + freightByOrderId;
            } else {
              return total;
            }
          },
          0,
        );

        // totalFreightIncome is already a converted currency amount
        const amount = totalFreightIncome * (extraFreight.amount / 100);

        incomes.push({
          type: 'EXTRA_FREIGHT',
          name: extraFreight.name ?? `Extra freight ${extraFreight.id}`,
          value: amount,
          orderId: null,
          voyageCharterId: voyageCharter.id,
          params: [
            {
              name: 'Extra freight type',
              value: 'Percent',
            },
            {
              name: 'Percent',
              value: extraFreight.amount,
            },
            {
              name: 'Description',
              value: extraFreight.description,
            },
          ],
        });
      }
    }
  });

  return incomes;
}
